import React, { useMemo } from 'react';
import type { Theme } from '../../types/game.types';
import type { FrontendMonster } from '../../types/monster.types';
import type { MonsterDefinition, MonsterTypeId } from '../../types/monster.types';
import { getMonsterNames } from '../../utils/constants';
import { getMonster } from '../../../utils/images';

import './MonsterList.css';

type MonsterCircleMonster = FrontendMonster | MonsterDefinition;

export interface MonsterCircleProps {
    monster: MonsterCircleMonster;
    onClick: () => void;
    disabled: boolean;
    hidden?: boolean;
    /**
     * Applied to the clickable wrapper, used for cursor + opacity.
     * Example: { cursor: 'pointer', opacity: 0.75 }
     */
    style?: React.CSSProperties;
    /** Extra style for the circular image container (e.g. filter/boxShadow). */
    imageStyle?: React.CSSProperties;
    theme: Theme;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

function getMonsterTypeId(monster: MonsterCircleMonster): MonsterTypeId {
    // FrontendMonster: { type } ; MonsterDefinition: { id }
    const anyMonster = monster as FrontendMonster & Partial<MonsterDefinition>;
    const typeId = anyMonster.type ?? anyMonster.id;
    return typeId as MonsterTypeId;
}

export const MonsterCircle: React.FC<MonsterCircleProps> = ({
    monster,
    onClick,
    disabled,   
    hidden = false,
    style,
    imageStyle,
    theme,
    onMouseEnter,
    onMouseLeave,
}) => {
    const monsterTypeId = useMemo(() => getMonsterTypeId(monster), [monster]);

    const sideImageUrl = useMemo(() => {
        if (hidden) return getMonster(theme, 'general_side.png');
        const info = getMonsterNames(monsterTypeId);
        const urlSlug = info?.url ?? 'general';
        const filename = `${urlSlug}_side.png`;
        return getMonster(theme, filename);
    }, [monsterTypeId, theme]);

    return (
        <div
            className="monster_option_square_container"
            style={style}
            onClick={() => {
                if (disabled) return;
                onClick();
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            aria-disabled={disabled}
        >
            <div className="monster_option_image_container" style={imageStyle}>
                <div className="monster_option_background">
                    <div
                        className="monster_option_image"
                        style={{
                            backgroundImage: `url(${sideImageUrl})`,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MonsterCircle;

